import { chiSquareUpper } from "@/lib/stats/distributions";

/**
 * Hardy–Weinberg arithmetic for one locus with two alleles: A (frequency p) and
 * a (frequency q = 1 − p). At equilibrium the genotypes AA, Aa and aa occur at
 * p², 2pq and q².
 */

export type KnownKind = "p" | "q" | "pp" | "pq" | "qq";

export interface Equilibrium {
  p: number;
  q: number;
  /** p², the AA frequency. */
  pp: number;
  /** 2pq, the Aa frequency. */
  pq: number;
  /** q², the aa frequency. */
  qq: number;
}

export type SolveOutcome =
  | { ok: true; solutions: Equilibrium[] }
  | { ok: false; reason: "range" | "heterozygote-range" };

export function equilibriumFromP(p: number): Equilibrium {
  const q = 1 - p;
  return { p, q, pp: p * p, pq: 2 * p * q, qq: q * q };
}

/**
 * Solve for the allele frequencies from one known quantity.
 *
 * A known heterozygote frequency has two solutions, because p and q can swap places
 * (2pq is symmetric), and it cannot exceed 1/2, the maximum of 2p(1 − p).
 */
export function solveFromKnown(kind: KnownKind, value: number): SolveOutcome {
  if (!(value >= 0 && value <= 1)) {
    return { ok: false, reason: "range" };
  }
  switch (kind) {
    case "p":
      return { ok: true, solutions: [equilibriumFromP(value)] };
    case "q":
      return { ok: true, solutions: [equilibriumFromP(1 - value)] };
    case "pp":
      return { ok: true, solutions: [equilibriumFromP(Math.sqrt(value))] };
    case "qq":
      return {
        ok: true,
        solutions: [equilibriumFromP(1 - Math.sqrt(value))],
      };
    default: {
      if (value > 0.5) {
        return { ok: false, reason: "heterozygote-range" };
      }
      const root = Math.sqrt(1 - 2 * value);
      const high = (1 + root) / 2;
      const low = (1 - root) / 2;
      const solutions =
        root === 0
          ? [equilibriumFromP(high)]
          : [equilibriumFromP(high), equilibriumFromP(low)];
      return { ok: true, solutions };
    }
  }
}

/**
 * A frequency typed as a decimal (0.04), a percentage (4%), or a fraction (1/2500).
 * `parse` reads one number the way the page's locale writes it.
 */
export function parseFrequency(
  text: string,
  parse: (raw: string) => number | null
): number | null {
  const trimmed = text.trim();
  if (trimmed === "") {
    return null;
  }
  if (trimmed.endsWith("%")) {
    const percent = parse(trimmed.slice(0, -1));
    return percent === null ? null : percent / 100;
  }
  const slash = trimmed.indexOf("/");
  if (slash !== -1) {
    const top = parse(trimmed.slice(0, slash));
    const bottom = parse(trimmed.slice(slash + 1));
    if (top === null || bottom === null || bottom === 0) {
      return null;
    }
    return top / bottom;
  }
  return parse(trimmed);
}

export interface HardyWeinbergTest {
  n: number;
  p: number;
  q: number;
  observed: [number, number, number];
  expected: [number, number, number];
  contributions: [number, number, number];
  chiSquare: number;
  /** One degree of freedom: three classes, minus one for the total, minus one for p. */
  df: 1;
  pValue: number;
  observedHeterozygosity: number;
  expectedHeterozygosity: number;
  /** (He − Ho) / He; positive for a shortage of heterozygotes. Null when He is 0. */
  inbreedingCoefficient: number | null;
  smallestExpected: number;
  /** Exact p-value, or null for a sample too large to enumerate. */
  exactP: number | null;
}

export type TestOutcome =
  | { ok: true; result: HardyWeinbergTest }
  | { ok: false; reason: "empty" | "monomorphic" };

/** Largest sample for which the exact test enumerates every heterozygote count. */
export const EXACT_TEST_LIMIT = 2_000_000;

/**
 * Exact test of Hardy–Weinberg equilibrium (Wigginton, Cutler & Abecasis 2005, Am J
 * Hum Genet 76:887–893): the p-value is the total probability of every heterozygote
 * count, given the allele counts, that is no more likely than the one observed.
 */
export function hardyWeinbergExact(
  hom1: number,
  hets: number,
  hom2: number
): number {
  const commonHom = Math.max(hom1, hom2);
  const rareHom = Math.min(hom1, hom2);
  const rareCopies = 2 * rareHom + hets;
  const n = hets + commonHom + rareHom;
  const probs = new Array<number>(rareCopies + 1).fill(0);
  let mid = Math.floor((rareCopies * (2 * n - rareCopies)) / (2 * n));
  if (mid % 2 !== rareCopies % 2) {
    mid += 1;
  }
  const startRare = (rareCopies - mid) / 2;
  const startCommon = n - mid - startRare;
  probs[mid] = 1;
  let sum = 1;

  let currRare = startRare;
  let currCommon = startCommon;
  for (let h = mid; h > 1; h -= 2) {
    probs[h - 2] =
      (probs[h] * h * (h - 1)) / (4 * (currRare + 1) * (currCommon + 1));
    sum += probs[h - 2];
    currRare += 1;
    currCommon += 1;
  }
  currRare = startRare;
  currCommon = startCommon;
  for (let h = mid; h <= rareCopies - 2; h += 2) {
    probs[h + 2] = (probs[h] * 4 * currRare * currCommon) / ((h + 2) * (h + 1));
    sum += probs[h + 2];
    currRare -= 1;
    currCommon -= 1;
  }
  const observed = probs[hets] / sum;
  let total = 0;
  for (const weight of probs) {
    const probability = weight / sum;
    if (probability <= observed * (1 + 1e-9)) {
      total += probability;
    }
  }
  return Math.min(total, 1);
}

/** Test observed genotype counts (AA, Aa, aa) against Hardy–Weinberg proportions. */
export function testHardyWeinberg(
  homDominant: number,
  hets: number,
  homRecessive: number
): TestOutcome {
  const n = homDominant + hets + homRecessive;
  if (n <= 0) {
    return { ok: false, reason: "empty" };
  }
  const p = (2 * homDominant + hets) / (2 * n);
  const q = 1 - p;
  if (p === 0 || q === 0) {
    return { ok: false, reason: "monomorphic" };
  }
  const observed: [number, number, number] = [homDominant, hets, homRecessive];
  const expected: [number, number, number] = [
    p * p * n,
    2 * p * q * n,
    q * q * n,
  ];
  const contributions = observed.map(
    (o, i) => (o - expected[i]) ** 2 / expected[i]
  ) as [number, number, number];
  const chiSquare = contributions[0] + contributions[1] + contributions[2];
  const expectedHeterozygosity = 2 * p * q;
  const observedHeterozygosity = hets / n;
  return {
    ok: true,
    result: {
      n,
      p,
      q,
      observed,
      expected,
      contributions,
      chiSquare,
      df: 1,
      pValue: chiSquareUpper(chiSquare, 1),
      observedHeterozygosity,
      expectedHeterozygosity,
      inbreedingCoefficient:
        expectedHeterozygosity > 0
          ? (expectedHeterozygosity - observedHeterozygosity) /
            expectedHeterozygosity
          : null,
      smallestExpected: Math.min(...expected),
      exactP:
        n <= EXACT_TEST_LIMIT
          ? hardyWeinbergExact(homDominant, hets, homRecessive)
          : null,
    },
  };
}

/** "1 in 25.5": the reciprocal of a frequency, or null for zero. */
export function oneIn(frequency: number): number | null {
  return frequency > 0 ? 1 / frequency : null;
}
